import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldOff, ArrowLeft, Home, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { notifyError, notifySuccess } from '@/utils/notify';

interface AcessoNegadoProps {
  titulo?: string;
  descricao?: string;
  rolesNecessarias?: string[];
  rolesAtuais?: string[];
}

const AcessoNegado: React.FC<AcessoNegadoProps> = ({
  titulo = 'Acesso negado',
  descricao = 'Você não possui as permissões necessárias para visualizar este recurso.',
  rolesNecessarias,
  rolesAtuais,
}) => {
  const navigate = useNavigate();
  const { user, tenantId } = useAuth();
  const [copiado, setCopiado] = useState(false);

  const infoSuporte = [
    `User ID: ${user?.id ?? 'N/D'}`,
    tenantId ? `Tenant ID: ${tenantId}` : null,
    `Rota: ${window.location.pathname}`,
    `Data: ${new Date().toLocaleString('pt-BR')}`,
  ].filter(Boolean).join('\n');

  const copiarInfo = async () => {
    try {
      await navigator.clipboard.writeText(infoSuporte);
      setCopiado(true);
      notifySuccess('Informações copiadas');
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      notifyError('Não foi possível copiar');
    }
  };

  const rolesFaltantes = rolesNecessarias?.filter(
    (r) => !(rolesAtuais ?? []).includes(r),
  ) ?? [];

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <Card className="w-full max-w-md border-destructive/30">
        <CardHeader className="items-center text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
            <ShieldOff className="h-7 w-7 text-destructive" aria-hidden="true" />
          </div>
          <CardTitle className="text-2xl">{titulo}</CardTitle>
          <CardDescription className="text-base">{descricao}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {rolesNecessarias && rolesNecessarias.length > 0 && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="mb-2 font-medium text-foreground">Perfis autorizados:</p>
              <div className="flex flex-wrap gap-1.5">
                {rolesNecessarias.map((role) => {
                  const possui = (rolesAtuais ?? []).includes(role);
                  return (
                    <span
                      key={role}
                      className={
                        possui
                          ? 'rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400'
                          : 'rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-medium text-destructive'
                      }
                    >
                      {possui ? '✓ ' : '✗ '}
                      {role}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {rolesAtuais && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="mb-2 font-medium text-foreground">Seus perfis atuais:</p>
              {rolesAtuais.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Nenhum perfil atribuído à sua conta.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {rolesAtuais.map((role) => (
                    <span
                      key={role}
                      className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {rolesFaltantes.length > 0 && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <p className="mb-1 font-medium text-destructive">
                Você precisa de pelo menos um destes perfis:
              </p>
              <p className="text-xs text-muted-foreground">
                {rolesFaltantes.join(', ')}
              </p>
            </div>
          )}

          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="font-medium text-foreground">Informações para suporte:</p>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={copiarInfo}
                aria-label="Copiar informações de suporte"
              >
                {copiado ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                <span className="ml-1 text-xs">{copiado ? 'Copiado' : 'Copiar'}</span>
              </Button>
            </div>
            <div className="space-y-1 font-mono text-xs text-muted-foreground">
              <p className="break-all"><span className="text-foreground">User ID:</span> {user?.id ?? 'N/D'}</p>
              {tenantId && (
                <p className="break-all"><span className="text-foreground">Tenant ID:</span> {tenantId}</p>
              )}
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            Caso acredite que isso é um engano, entre em contato com o administrador do sistema informando os dados acima e solicitando a atribuição de um dos perfis listados.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="flex-1" onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
            <Button className="flex-1" onClick={() => navigate('/dashboard')}>
              <Home className="mr-2 h-4 w-4" />
              Ir para o Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcessoNegado;
